<template>
  <c-box data-cy="material_container" marginTop="2xl">
    <c-heading as="h1" fontSize="4xl" fontWeight="normal" marginTop="4xl" marginBottom="2xl">{{
      currentCategory ? translatedCategoryName(currentCategory) : $t("material.allCategories")
    }}</c-heading>
    <c-box class="row" justifyContent="space-between" marginTop="2xl" marginBottom="2xl">
      <c-box class="col-12 col-sm-7" marginTop="2xl" marginBottom="2xl">
        <c-input-group>
          <c-input-left-addon
            pointerEvents="none"
            background="transparent"
            border="0"
            paddingLeft="0"
          >
            <c-icon name="search" size="2rem" color="brand.gray.light" mt="0.5rem" />
          </c-input-left-addon>
          <c-input
            id="searchTerm"
            v-model.trim="searchTerm"
            type="text"
            autocomplete="off"
            :placeholder="$t('material.search')"
            @search="handleSearchInput(searchTerm)"
            @keydown="handleSearchInput(searchTerm)"
            @keyup="handleSearchInput(searchTerm)"
            @blur="handleSearchInput(searchTerm)"
            borderRadius="0"
            border="0"
            background="transparent"
            px="0"
            border-bottom="1px solid"
            border-width="0"
            border-bottom-width="1px"
            border-color="transparent"
            border-bottom-color="brand.purple"
            focus-border-color="transparent"
            :_hover="{ borderBottomColor: 'brand.purple' }"
            :_focus="{ borderBottomColor: 'brand.purple' }"
            :_active="{ borderBottomColor: 'brand.purple' }"
          />
        </c-input-group>
      </c-box>
      <c-box
        v-if="
          items.customLists?.Language &&
          Object.keys(items.customLists?.Language) &&
          currentCategory !== priceListCategory
        "
        class="col-12 col-sm-4"
        marginTop="2xl"
        marginBottom="2xl"
      >
        <c-select
          id="language-select"
          v-model="selectedLanguage"
          @change="updateLanguageFilter"
          data-cy="material_language_select"
          float="right"
          size="sm"
          borderRadius="full"
          background="white"
          borderColor="brand.purple"
          :maxWidth="{ base: '100%', sm: '250px' }"
        >
          <option for="language-select" value="">
            {{
              parseInt(selectedLanguage, 10) > 0
                ? $t("material.allLanguages")
                : $t("material.chooseLanguage")
            }}
          </option>
          <option
            for="language-select"
            v-for="language in filteredLanguages"
            :key="`language-${language}`"
            :value="language"
          >
            {{ language }}
          </option>
        </c-select>
      </c-box>
    </c-box>
    <c-box class="row" marginTop="2xl" marginBottom="2xl">
      <c-box class="col-12">
        <c-text as="label" for="category-select" marginRight="xl">{{
          $t("material.categories")
        }}</c-text>
        <c-button-group flexWrap="wrap" size="sm">
          <c-button
            :background-color="currentSubcategory === '' ? 'black' : 'white'"
            :color="currentSubcategory === '' ? 'white' : 'black'"
            mb="2"
            px="md"
            @click="updateCategoryFilter('')"
          >
            {{ $t("material.allCategories") }}
          </c-button>
          <c-button
            v-for="subCategory in subCategories"
            :key="subCategory"
            :background-color="currentSubcategory === subCategory ? 'black' : 'white'"
            :color="currentSubcategory === subCategory ? 'white' : 'black'"
            mb="2"
            px="md"
            @click="updateCategoryFilter(subCategory)"
          >
            {{ translatedCategoryName(subCategory) }}
          </c-button>
        </c-button-group>
      </c-box>
    </c-box>
    <c-box>
      <c-box class="row" paddingBottom="xl" borderBottom="1px solid" borderBottomColor="brand.gray">
        <c-box class="col-12">
          <c-text v-if="items.files" as="span">
            {{ $t("material.results") }}: {{ filteredMaterial.length }} {{ $t("material.files") }}
          </c-text>
          <Spinner v-else size="sm"></Spinner>
          <c-box float="right">
            <c-button-group size="sm" isAttached>
              <c-button
                @click="changeView('list')"
                px="md"
                borderColor="brand.purple"
                :color="viewMode === 'list' ? 'white' : 'brand.purple'"
                :backgroundColor="viewMode === 'list' ? 'brand.purple' : 'white'"
              >
                <c-icon
                  name="list_layout"
                  size="1rem"
                  :color="viewMode === 'list' ? 'white' : 'black'"
                >
                </c-icon
              ></c-button>
              <c-button
                @click="changeView('grid')"
                px="md"
                borderColor="brand.purple"
                :color="viewMode === 'grid' ? 'white' : 'brand.purple'"
                :backgroundColor="viewMode === 'grid' ? 'brand.purple' : 'white'"
              >
                <c-icon
                  name="grid_layout"
                  size="1rem"
                  :color="viewMode === 'grid' ? 'white' : 'black'"
                ></c-icon>
              </c-button>
            </c-button-group>
          </c-box>
        </c-box>
      </c-box>
      <c-box v-if="items.files" :class="{ row: viewMode === 'grid' }" paddingTop="xl">
        <c-box
          v-for="material in filteredMaterial"
          :key="material.id"
          data-cy="material_row_container"
          :class="{
            row: viewMode === 'list',
            'col-12 col-md-6 col-lg-3 col-xl-2': viewMode === 'grid',
          }"
          :display="viewMode === 'grid' ? 'flex' : 'block'"
          :flex-direction="viewMode === 'grid' ? 'column' : 'inherit'"
          :alignContent="viewMode === 'list' ? 'center' : 'inherit'"
          :alignItems="viewMode === 'list' ? 'center' : 'inherit'"
          :border-bottom="viewMode === 'list' ? '1px solid' : '0'"
          borderBottomColor="brand.gray"
          paddingTop="base"
          paddingBottom="base"
        >
          <c-box :class="{ 'col-4 col-md-1': viewMode === 'list', 'my-2': viewMode === 'grid' }">
            <img :src="getThumbnail(material)" :alt="material.fileName" />
          </c-box>
          <c-box
            :class="{ 'col-8 col-md-11': viewMode === 'list' }"
            :display="viewMode === 'grid' ? 'grid' : 'block'"
            :flex-grow="viewMode === 'grid' ? '1' : 'inherit'"
          >
            <c-box
              :class="{ row: viewMode === 'list' }"
              :alignContent="viewMode === 'list' ? 'center' : 'inherit'"
              :alignItems="viewMode === 'list' ? 'center' : 'inherit'"
              :display="viewMode === 'grid' ? 'grid' : 'block'"
              :flex-grow="viewMode === 'grid' ? '1' : 'inherit'"
            >
              <c-box :class="{ 'col-12 col-md-7': viewMode === 'list' }" fontSize="lg">
                <c-tooltip has-arrow :label="material.fileName" placement="bottom">
                  <c-text
                    as="span"
                    v-chakra="{
                      color: 'brand.purple',
                      display: 'block',
                      wordWrap: 'break-word',
                      whiteSpace: 'pre-wrap',
                      fontSize: 'lg',
                    }"
                    :fontWeight="viewMode === 'grid' ? 'bold' : 'normal'"
                    >{{ cutTheLongName(material.fileName || "") }}</c-text
                  >
                </c-tooltip>
              </c-box>
              <c-box
                :class="{ 'col-5 col-md-2': viewMode === 'list' }"
                :marginBottom="viewMode === 'grid' ? 'xl' : '0'"
                v-chakra="{
                  display: 'block',
                  color: 'brand.purple',
                }"
              >
                <c-text as="span">{{ $t("material.format") }}:</c-text>
                <c-text
                  as="span"
                  color="brand.purple"
                  :marginLeft="viewMode === 'grid' ? 'xs' : '0'"
                  :display="viewMode === 'grid' ? 'inline-block' : 'block'"
                >
                  <c-text as="span" textTransform="uppercase" data-cy="file_extension">
                    {{ regEx(material.fileName || "") }}
                  </c-text>
                  -{{ $t("material.file") }}
                </c-text>
              </c-box>
              <c-box
                :class="{ 'text-right col-7 col-md-3': viewMode === 'list' }"
                :marginBottom="viewMode === 'grid' ? '2xl' : ''"
                :alignSelf="viewMode === 'grid' ? 'flex-end' : 'none'"
              >
                <c-button
                  variation="primary"
                  as="a"
                  :href="material.originalUrl"
                  :download="material.fileName"
                  :target="'_blank'"
                  backgroundColor="brand.beige"
                  color="brand.purple"
                  border-color="brand.beige"
                  cursor="pointer"
                  borderStyle="solid"
                  :hover="{ backgroundColor: 'brand.beigeLight' }"
                  align="right"
                  data-cy="downloadButton"
                  marginTop="base"
                  marginBottom="base"
                >
                  <c-icon name="download" size="1rem" marginRight="0.25rem" color="black" />
                  {{ $t("material.download") }}
                </c-button>
              </c-box>
            </c-box>
          </c-box>
        </c-box>
      </c-box>
      <c-flex justify-content="center" align-items="center" height="25vh" v-else>
        <Spinner size="lg" />
      </c-flex>
    </c-box>
  </c-box>
</template>

<script lang="ts">
import { IMaterial } from "@iloq/shared";
import config from "@/config";
import { defineComponent } from "vue";
import { mapState } from "vuex";
import {
  CHeading,
  CBox,
  CFlex,
  CText,
  CButtonGroup,
  CButton,
  CInputGroup,
  CInput,
  CInputLeftAddon,
  CIcon,
  CSelect,
  CTooltip,
} from "@chakra-ui/vue";
import Spinner from "@/components/Spinner.vue";
import { IRootState } from "@iloq/shared";

export default defineComponent({
  name: "Material",
  components: {
    CButtonGroup,
    CHeading,
    CBox,
    CFlex,
    CText,
    CButton,
    CInputGroup,
    CInputLeftAddon,
    CInput,
    CIcon,
    CSelect,
    CTooltip,
    Spinner,
  },
  data() {
    return {
      searchTerm: "",
      viewMode: "grid",
      selectedLanguage: "",
      search: "",
      priceListCategory: config.get("materialDamPriceListCategory"),
    };
  },
  computed: {
    ...mapState({
      user: (state: unknown) => (state as IRootState).user!,
    }),
    ...mapState("material", {
      items: "items",
    }),
    subCategories() {
      return [
        ...new Set(
          this.categoryMaterial
            ?.flatMap((item) => item.tags || [])
            .filter((tag) => tag !== this.currentCategory) || [],
        ),
      ];
    },
    currentCategory() {
      return this.$route.query.category || "";
    },
    currentSubcategory() {
      return this.$route.query.subCategory || "";
    },
    categoryMaterial() {
      let filteredItems = this.items.files || [];
      if (this.currentCategory && filteredItems.length > 0) {
        filteredItems = filteredItems?.filter(
          (item: IMaterial) => item.tags?.some((c: string) => c === this.currentCategory) ?? false,
        );
      }
      return filteredItems;
    },
    filteredMaterial() {
      let filteredItems = this.categoryMaterial || [];
      if (this.currentSubcategory) {
        filteredItems = filteredItems?.filter(
          (item: IMaterial) =>
            item.tags?.some((c: string) => c === this.currentSubcategory) ?? false,
        );
      }
      if (this.selectedLanguage) {
        filteredItems = filteredItems?.filter((item) => {
          return (
            !item.customData ||
            item.customData.some(
              (data) =>
                data.name === "Language" && data.selectedListItems?.includes(this.selectedLanguage),
            )
          );
        });
      }
      if (this.currentCategory === this.priceListCategory) {
        const materialDamMappingList = config.get("materialDamMappingList");
        const locale = this.user.groups.includes("Country_OTHER")
          ? "OTHER"
          : this.user.countryCode.toUpperCase();
        const mappedMaterialLocale = Object.keys(materialDamMappingList).find(
          (key) => materialDamMappingList[key] === locale,
        );
        filteredItems = filteredItems?.filter((item) => {
          return (
            item.customData &&
            item.customData.some(
              (data) =>
                data.name === "Locale" && data.selectedListItems.includes(mappedMaterialLocale),
            )
          );
        });
      }
      if (this.search) {
        const searchTerm = this.search.trim().toLowerCase();
        filteredItems = filteredItems?.filter(
          (item: IMaterial) =>
            item.fileName?.toLowerCase().includes(searchTerm) ||
            item.metaData?.title?.toLowerCase().includes(searchTerm) ||
            item.metaData?.description?.toLowerCase().includes(searchTerm),
        );
      }
      return filteredItems;
    },
    filteredLanguages() {
      const languages = this.categoryMaterial.flatMap(
        (item) =>
          item.customData?.find((data) => data.name === "Language")?.selectedListItems || [],
      );
      return Array.from(new Set(languages));
    },
  },
  methods: {
    updateLanguageFilter(languageId) {
      this.selectedLanguage = languageId;
    },
    handleSearchInput(keyword: string) {
      this.search = keyword;
    },
    changeView(handle: string) {
      if (this.viewMode === handle) {
        return;
      }
      this.viewMode = handle;
    },
    updateCategoryFilter(subCategory: string, category?: string) {
      this.$router.push(
        `${this.$route.path}?category=${category || this.currentCategory}&subCategory=${subCategory}`,
      );
    },
    translatedCategoryName(categoryName: string) {
      const translationKey = `material.category.${categoryName.toLowerCase().replace(/[^a-z0-9]+/g, "")}`;
      const translatedName =
        (this as any).$t(translationKey) === translationKey
          ? categoryName
          : (this as any).$t(translationKey);
      return translatedName;
    },
    cutTheLongName(value: string) {
      const whitespaced = value.replace(/_/g, " ");
      if (whitespaced.length > 50) {
        const trimmed = whitespaced.substring(0, 50);
        const lastSpace = trimmed.lastIndexOf(" ");
        const cutoffIndex = Math.min(lastSpace >= 0 && lastSpace < 50 ? lastSpace : Infinity, 50);

        return trimmed.substring(0, cutoffIndex) + "…";
      } else {
        return whitespaced;
      }
    },
    getThumbnail(material: IMaterial) {
      return material?.thumbnails?.[300] || "";
    },
    regEx(value: string) {
      const re = /(?:\.([^.]+))?$/;
      const res = re.exec(value);
      if (res) {
        return res[0].replace(/^\./, "");
      }
      return "";
    },
    userLocale(): string {
      return `${this.user.languageCode}_${this.user.countryCode.toUpperCase()}`;
    },
  },
  watch: {
    currentCategory(newCategory) {
      if (
        !this.filteredLanguages.includes(this.selectedLanguage) ||
        newCategory === this.priceListCategory
      ) {
        this.selectedLanguage = "";
      }
    },
  },
});
</script>
